import { DB } from '@services/database.service';
import { useUIStore } from '@stores/v4/useUIStore';
import { useAthleteStore } from '@stores/v4/useAthleteStore';
import { useHistoryStore } from '@stores/v4/useHistoryStore';
import { AIWorkoutService } from '@ai-services/ai-workout.service';

/**
 * HISTORY ORCHESTRATOR
 * Handles complex async I/O and cross-store side-effects for event history.
 * HARDENED: Modularized to reduce Store complexity (< 150 lines).
 */
export const HistoryOrchestrator = {
    /**
     * fetchSessionDetails: Hydrates deep log data for a completed session.
     */
    async fetchSessionDetails(sessionId: string) {
        const fkConstraint = useUIStore.getState().getFKConstraint();
        useUIStore.getState().setIsLoading(true);
        try {
            const { data: logs, error: lErr } = await DB.fetchSessionDetails(sessionId, fkConstraint);
            if (lErr) throw lErr;

            const groups: any[] = [];
            (logs as any[] || []).forEach(log => {
                const exName = log.block_items?.exercise_library?.name || log.exercise_name_snapshot || 'Unknown Exercise';
                const targets = {
                    w: log.block_items?.target_weight || (log.target_snapshot || '?'),
                    r: log.block_items?.target_reps || '?',
                    e: log.block_items?.target_rpe || '?',
                    t: log.block_items?.tempo || ''
                };

                const lastGroup = groups[groups.length - 1];
                if (!lastGroup || lastGroup.name !== exName) {
                    groups.push({ name: exName, targets, sets: [log] });
                } else {
                    lastGroup.sets.push(log);
                }
            });

            const { data: header } = await DB.fetchCompletedSessionHeader(sessionId);
            useHistoryStore.setState({ 
                activeHistorySession: header ? { ...header, groupedLogs: groups } : null 
            });
        } catch (err) {
            console.error("[HistoryOrchestrator] fetchSessionDetails FAILED:", err);
        } finally {
            useUIStore.getState().setIsLoading(false);
        }
    },

    /**
     * deleteSessionRecord: Orchestrates optimistic removal and DB cleanup.
     */
    async deleteSessionRecord(sessionId: string) {
        const history = useHistoryStore.getState();
        const linkedEvent = history.events.find(e => e.completed_session_id === sessionId);
        
        if (linkedEvent) history.removeOptimisticEvent(linkedEvent.id);

        try {
            const { error } = await DB.deleteSessionRecord(sessionId);
            if (error) throw error;
        } catch (err) {
            console.error("[HistoryOrchestrator] deleteSessionRecord FAILED:", err);
            await history.fetchGlobalHistory(true); // Revert on failure
            throw err;
        }
    },

    /**
     * deleteEvent: Orchestrates optimistic removal and DB cleanup.
     */
    async deleteEvent(eventId: string) {
        const history = useHistoryStore.getState();
        history.removeOptimisticEvent(eventId);
        try {
            const { error } = await DB.deleteUnifiedEvent(eventId);
            if (error) throw error;
        } catch (err) {
            console.error("[HistoryOrchestrator] deleteEvent FAILED:", err);
            await history.fetchGlobalHistory(true);
        }
    },

    /**
     * recalculateBurn: Triggers AI analysis for a completed session.
     */
    async recalculateBurn(eventId: string) {
        const history = useHistoryStore.getState();
        const event = history.events.find(e => e.id === eventId);
        if (!event || !event.completed_session_id) return;

        useHistoryStore.setState(state => ({ 
            analyzingEvents: new Set(state.analyzingEvents).add(eventId) 
        }));
        
        try {
            const profile = useAthleteStore.getState().profile;
            const durationMinutes = Math.round((new Date(event.end_time || event.start_time).getTime() - new Date(event.start_time).getTime()) / 60000) || 60;
            
            const { data: details } = await DB.fetchSessionDetails(event.completed_session_id, useUIStore.getState().getFKConstraint());
            const { data: burnRes } = await AIWorkoutService.estimateSessionBurn(details || [], profile, durationMinutes);
            
            if (burnRes?.kcal_burn) {
                const currentPreview = (event.preview_data as any) || {};
                const updatedPreview = { 
                    ...currentPreview, 
                    estimated_burn: burnRes.kcal_burn,
                    burn_logic: burnRes.logic_summary 
                };

                await DB.updateUnifiedEvent(eventId, { preview_data: updatedPreview });
                history.updateOptimisticEvent(eventId, { preview_data: updatedPreview });
            }
        } catch (err) {
            console.error("[HistoryOrchestrator] recalculateBurn FAILED:", err);
        } finally {
            useHistoryStore.setState(state => {
                const newAnalyzing = new Set(state.analyzingEvents);
                newAnalyzing.delete(eventId);
                return { analyzingEvents: newAnalyzing };
            });
        }
    }
};
